import { useEffect, useMemo, useRef } from "react";
import useDevices from "hooks/livekit/useDevices/useDevices";
import { logLiveKit, LogLevel, setAudioDeviceContext } from "utils/sentryLogger";

// ponytail: label word-match. The browser has no "is this a headphone" flag;
// widen the lists when a real label slips through as "unknown".
const HEADPHONE_RE = /headphone|headset|earbud|earphone|airpod|bluetooth|usb/i;
const BUILTIN_RE = /internal|built-?in|speaker/i;

export const speakerKind = (label) => {
  if (!label) return "unknown";
  if (HEADPHONE_RE.test(label)) return "headphone";
  if (BUILTIN_RE.test(label)) return "builtin";
  return "unknown";
};

const labels = (devices) => devices.map((d) => d.label).filter(Boolean).join(" | ");

export const buildAudioDeviceSnapshot = ({ audioInputDevices, audioOutputDevices, sinkId, micLabel }) => {
  const speakerLabel = audioOutputDevices.find((d) => d.deviceId === sinkId)?.label || "";
  return {
    mic_label: micLabel || "",
    speaker_label: speakerLabel,
    speaker_kind: speakerKind(speakerLabel),
    sink_id: sinkId,
    audio_inputs: labels(audioInputDevices),
    audio_outputs: labels(audioOutputDevices),
  };
};

/**
 * Records which mic and speaker the student is on, and everything the browser
 * offered, every time that changes: join, permission grant, unplug/replug,
 * a mid-session switch. Labels are empty until mic permission is granted, so
 * the pre-permission state is skipped rather than logged as "nothing".
 */
export default function useAudioDeviceTelemetry({ localTracks, activeAudioSinkId, sessionId, userId, groupCode }) {
  const { audioInputDevices, audioOutputDevices } = useDevices();
  const micLabel = (localTracks || []).find((t) => t.kind === "audio")?.mediaStreamTrack?.label || "";

  const snapshot = useMemo(
    () => buildAudioDeviceSnapshot({ audioInputDevices, audioOutputDevices, sinkId: activeAudioSinkId, micLabel }),
    [audioInputDevices, audioOutputDevices, activeAudioSinkId, micLabel]
  );

  const lastLoggedRef = useRef("");
  useEffect(() => {
    if (!snapshot.mic_label && !snapshot.audio_inputs && !snapshot.audio_outputs) return;
    const key = JSON.stringify(snapshot);
    if (key === lastLoggedRef.current) return;
    lastLoggedRef.current = key;
    setAudioDeviceContext(snapshot);
    logLiveKit("Audio devices", {
      level: LogLevel.INFO,
      event: "audio_devices_snapshot",
      sessionId,
      userId,
      groupCode,
    });
  }, [snapshot, sessionId, userId, groupCode]);

  return snapshot;
}
